import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ENDGUELTIGE_FEHLER,
  webSpeechAdapter,
  type SpracherkennungAdapter,
  type SpracherkennungFehler,
  type SpracherkennungSitzung,
} from './web-speech';

/**
 * React-Anbindung der Spracherkennung — die einzige Stelle, an der ein
 * Erkenner-Adapter gestartet und gestoppt wird.
 *
 * Alles, was den Ablauf steuert, liegt in Refs statt im State: die Callbacks
 * des Erkenners laufen außerhalb des Render-Zyklus, ein `useState`-Wert wäre
 * dort veraltet. State gibt es nur für das, was gerendert wird.
 */

export type ErkennungStatus =
  | 'nicht-unterstuetzt'
  | 'inaktiv'
  | 'hoert-zu'
  | 'verarbeitet'
  | 'fehler';

export interface SpracherkennungHookOptionen {
  /** Wird je fertig erkannter Äußerung mit dem Rohtext aufgerufen. */
  onEndergebnis: (text: string, konfidenz: number) => void;
  sprache?: string;
  /** Weiterhören über Sprechpausen hinweg (Standard: ja). */
  dauerhaft?: boolean;
  /** Austauschbar für Tests und für einen späteren Server-Erkenner. */
  adapter?: SpracherkennungAdapter;
}

export interface Spracherkennung {
  verfuegbar: boolean;
  status: ErkennungStatus;
  fehler: SpracherkennungFehler | null;
  /** Was gerade gehört wird, noch nicht endgültig. */
  zwischentext: string;
  starte: () => void;
  stoppe: () => void;
  umschalten: () => void;
}

/**
 * Chrome beendet die Erkennung nach einer Sprechpause von selbst. Für
 * durchgehendes Zuhören starten wir neu — aber nicht endlos: nach drei
 * Neustarts ohne ein einziges Ergebnis geht das Mikrofon aus. Ein unbemerkt
 * offenes Mikrofon ist genau das, was niemand will.
 */
const MAX_LEERLAUF_NEUSTARTS = 3;
/** Kurze Pause vor dem Neustart — sonst wirft Chrome InvalidStateError. */
const NEUSTART_VERZOEGERUNG = 250;

export function useSpracherkennung(optionen: SpracherkennungHookOptionen): Spracherkennung {
  const {
    onEndergebnis,
    sprache = 'de-DE',
    dauerhaft = true,
    adapter = webSpeechAdapter,
  } = optionen;

  const [verfuegbar] = useState(() => adapter.verfuegbar());
  const [status, setStatus] = useState<ErkennungStatus>(() =>
    adapter.verfuegbar() ? 'inaktiv' : 'nicht-unterstuetzt',
  );
  const [fehler, setFehler] = useState<SpracherkennungFehler | null>(null);
  const [zwischentext, setZwischentext] = useState('');

  const sitzungRef = useRef<SpracherkennungSitzung | null>(null);
  const sollLaufenRef = useRef(false);
  const leerlaufRef = useRef(0);
  const hatErgebnisRef = useRef(false);
  const neustartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Der Callback wechselt bei jedem Render die Identität; über den Ref bleibt
  // die laufende Erkennung trotzdem am aktuellen Handler.
  const ergebnisRef = useRef(onEndergebnis);
  ergebnisRef.current = onEndergebnis;

  const starteSitzung = useCallback(() => {
    hatErgebnisRef.current = false;
    sitzungRef.current = adapter.starte({
      sprache,
      dauerhaft,
      zwischenergebnisse: true,
      onErgebnis: (ergebnis) => {
        hatErgebnisRef.current = true;
        leerlaufRef.current = 0;
        if (!ergebnis.final) {
          setZwischentext(ergebnis.text);
          return;
        }
        setZwischentext('');
        const text = ergebnis.text.trim();
        if (text !== '') ergebnisRef.current(text, ergebnis.konfidenz);
      },
      onFehler: (art) => {
        // „abgebrochen“ ist die normale Antwort auf unser eigenes Stoppen und
        // „nichts verstanden“ nur eine Pause — beides ist kein Fehlerzustand.
        if (art === 'abgebrochen' || art === 'nichts-verstanden') return;
        setFehler(art);
        setStatus('fehler');
        if (ENDGUELTIGE_FEHLER.includes(art)) sollLaufenRef.current = false;
      },
      onEnde: () => {
        sitzungRef.current = null;
        if (!sollLaufenRef.current) {
          setZwischentext('');
          setStatus((vorher) => (vorher === 'fehler' ? 'fehler' : 'inaktiv'));
          return;
        }
        leerlaufRef.current = hatErgebnisRef.current ? 0 : leerlaufRef.current + 1;
        if (leerlaufRef.current > MAX_LEERLAUF_NEUSTARTS) {
          sollLaufenRef.current = false;
          setFehler('nichts-verstanden');
          setStatus('fehler');
          return;
        }
        neustartTimerRef.current = setTimeout(() => {
          if (sollLaufenRef.current) starteSitzung();
        }, NEUSTART_VERZOEGERUNG);
      },
    });
  }, [adapter, dauerhaft, sprache]);

  const starte = useCallback(() => {
    if (sollLaufenRef.current) return;
    if (!verfuegbar) {
      setFehler('nicht-unterstuetzt');
      setStatus('nicht-unterstuetzt');
      return;
    }
    sollLaufenRef.current = true;
    leerlaufRef.current = 0;
    setFehler(null);
    setZwischentext('');
    setStatus('hoert-zu');
    starteSitzung();
  }, [starteSitzung, verfuegbar]);

  const stoppe = useCallback(() => {
    sollLaufenRef.current = false;
    if (neustartTimerRef.current) clearTimeout(neustartTimerRef.current);
    if (sitzungRef.current) {
      // Erst „verarbeitet“: ein zuletzt gesprochener Satz kommt noch nach.
      setStatus((vorher) => (vorher === 'fehler' ? 'fehler' : 'verarbeitet'));
      sitzungRef.current.stopp();
    } else {
      setStatus((vorher) => (vorher === 'fehler' ? 'fehler' : 'inaktiv'));
    }
  }, []);

  const umschalten = useCallback(() => {
    if (sollLaufenRef.current) stoppe();
    else starte();
  }, [starte, stoppe]);

  // Beim Verlassen der Seite muss das Mikrofon zuverlässig aus sein.
  useEffect(
    () => () => {
      sollLaufenRef.current = false;
      if (neustartTimerRef.current) clearTimeout(neustartTimerRef.current);
      sitzungRef.current?.abbrechen();
      sitzungRef.current = null;
    },
    [],
  );

  return { verfuegbar, status, fehler, zwischentext, starte, stoppe, umschalten };
}
